import { App as CapApp } from '@capacitor/app';

/**
 * Laddar om appen när en ny webbversion har driftsatts. iOS-appen laddar
 * qvenari.com men laddar inte om sidan när den kommer tillbaka från
 * bakgrunden, så utan detta körs gammal kod tills appen startas om.
 *
 * Vite bakar in byggets id (__APP_BUILD_ID__) och skriver samma id till
 * /version.json (vite.config.ts). När appen blir aktiv igen hämtas filen; skiljer
 * den sig laddas sidan om — men aldrig under en pågående jakt.
 */

declare const __APP_BUILD_ID__: string;
const CURRENT_BUILD_ID = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'dev';

export type UpdateDecision = 'reload' | 'defer' | 'none';

export function decideUpdate(current: string, latest: string | null, huntActive: boolean): UpdateDecision {
  if (!latest || latest === current) return 'none';
  return huntActive ? 'defer' : 'reload';
}

let activeHunts = 0;

/** KartaTab anropar med true när en jakt (LiveKit-session) startar och false när den slutar. */
export function setHuntActive(active: boolean): void {
  activeHunts = Math.max(0, activeHunts + (active ? 1 : -1));
}

async function fetchLatestBuildId(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const json = await res.json() as { buildId?: unknown };
    return typeof json.buildId === 'string' ? json.buildId : null;
  } catch {
    return null; // offline i skogen — försök igen nästa gång
  }
}

let checking = false;

export async function checkForUpdate(): Promise<UpdateDecision> {
  if (checking) return 'none';
  checking = true;
  try {
    const decision = decideUpdate(CURRENT_BUILD_ID, await fetchLatestBuildId(), activeHunts > 0);
    if (decision === 'reload') {
      console.log('[QVENARI] Ny version driftsatt — laddar om');
      window.location.reload();
    } else if (decision === 'defer') {
      console.log('[QVENARI] Ny version finns — väntar tills jakten är slut');
    }
    return decision;
  } finally {
    checking = false;
  }
}

/** Kollar efter ny version varje gång appen/fliken blir aktiv igen. */
export function installUpdateCheck(): void {
  if (import.meta.env.DEV || CURRENT_BUILD_ID === 'dev') return;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });
  if ((window as any).Capacitor?.isNativePlatform?.()) {
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) void checkForUpdate();
    }).catch(() => {});
  }
}
